import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref, Ref, ShallowRef, shallowRef, watch } from "vue";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import {
    DRILLDOWN_COUNTRY_LEVEL_NUMBER,
    DRILLDOWN_LEAGUE_LEVEL_NUMBER,
    DRILLDOWN_SPORT_LEVEL_NUMBER,
} from "@/shared/utils/constants";
import { omit as _omit, orderBy as _orderBy, sortBy as _sortBy, sortedUniqBy as _sortedUniqBy } from "lodash-es";
import DrilldownNodeUtils from "@/shared/utils/drilldown-node-utils";
import { isDevelopmentEnv } from "@/shared/utils/environment-variables";
import { waitUntilTrue } from "@/shared/utils/wait";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { Sport } from "@/shared/model/enums/sport";
import { computedAsync } from "@vueuse/core";

// Remove useless data which we don't use and just takes place in the store... this should be somewhere on api level to remove useless data asap
const removeUseless = (drilldownNode: DrilldownNode): DrilldownNode =>
    _omit(drilldownNode, ["refRecordId", "refRecordType"]);

const flattenTree = (drilldownNodes: DrilldownNode[], stripChildren: boolean = false): DrilldownNode[] =>
    stripChildren
        ? drilldownNodes?.flatMap(({ drilldownNodes = [], ...rest }) =>
              Array.isArray(drilldownNodes) ? [rest, ...flattenTree(drilldownNodes, stripChildren)] : [],
          ) || []
        : drilldownNodes?.flatMap((d) =>
              Array.isArray(d?.drilldownNodes) ? [d, ...flattenTree(d.drilldownNodes)] : [d],
          ) || [];

export const useDrilldownNodesStore = defineStore("DrilldownNodes", () => {
    const fetchInterval: Ref<number> = ref(120000);
    const lastFetched: Ref<number> = ref();
    const fetchInProgress: Ref<boolean> = ref(false);
    const initialized: Ref<boolean> = ref(false);
    // We use shallowRef here to reduce reactivity overhead here... we want to re-compute all computed properties only when the whole dataset changes.
    const drilldownNodesTree: ShallowRef<DrilldownNode[]> = shallowRef([]);
    let intervalId = null;

    const { getDrilldownNodes } = useOpenbetApi();

    getDrilldownNodes()
        .then((response: DrilldownNode[]) => {
            drilldownNodesTree.value = response || [];
            lastFetched.value = performance.now();
        })
        .finally(() => {
            initialized.value = true;
        });

    watch(
        fetchInterval,
        (newValue) => {
            if (newValue) {
                intervalId = setInterval(async () => {
                    await fetchDrilldownNodes();
                }, fetchInterval.value);
            } else if (intervalId) {
                clearInterval(intervalId);
            }
        },
        { immediate: true },
    );

    // All drilldown nodes in flattened structure include their children (drilldownNodes keys)
    const drilldownNodesFlat = computed(() =>
        _sortBy(flattenTree(drilldownNodesTree.value), (item) => Number(item.id)),
    );

    // All drilldown nodes in flattened structure without their children (drilldownNodes keys) and with useless data cleaned... makes it nice and lightweight
    const all = computed(() =>
        _sortBy(
            flattenTree(drilldownNodesTree.value, true)?.map((d) => removeUseless(d)),
            (item) => Number(item.id),
        ),
    );

    const sports = computed(() =>
        _sortedUniqBy(
            all.value.filter((d) => d?.levelNumber === DRILLDOWN_SPORT_LEVEL_NUMBER),
            "id",
        ),
    );
    const countries = computed(() =>
        _sortedUniqBy(
            all.value.filter((d) => d?.levelNumber === DRILLDOWN_COUNTRY_LEVEL_NUMBER),
            "id",
        ),
    );
    const leagues = computed(() =>
        _sortedUniqBy(
            all.value.filter((d) => d?.levelNumber === DRILLDOWN_LEAGUE_LEVEL_NUMBER),
            "id",
        ),
    );

    const priceBoostId: Ref<string> = computedAsync(async () =>
        initialized.value ? await getSportPriceBoostId() : undefined,
    );

    // Will return all drilldown nodes array, built from bottom to top from the input parameter 'id'
    const getDrilldownNodesById = async (id: string, includeRootNode: boolean = false) => {
        await waitUntilInitialized();
        // If id is not found in all drilldown nodes it triggers fresh data fetch out of 'refresh' cycle
        if (!all.value.some((i) => i.id === id)) {
            // BEWARE: It can happen that /openbet/content-service/q/sazka-drilldown-nodes does not serve all drilldown nodes (especially those with the highest ids),
            // which leads to situations where data from this ep are continually fetched. :(
            if (isDevelopmentEnv()) console.log("Unable to find drilldown node id ", id);
            await fetchDrilldownNodes();
        }
        const drilldownNodeIds = DrilldownNodeUtils.buildBranchPath(all.value, id);
        if (!includeRootNode) {
            drilldownNodeIds.shift();
        }
        return _orderBy(
            all.value.filter((node) => drilldownNodeIds.includes(node.id)),
            "levelNumber",
            "desc",
        );
    };
    const getAllById = async (id: string) => {
        await waitUntilInitialized();
        if (!all.value.some((i) => i.id === id)) {
            await fetchDrilldownNodes();
        }
        return all.value.find((i) => i.id === id);
    };
    const getSportById = async (id: string) => {
        await waitUntilInitialized();
        if (!sports.value.some((i) => i.id === id)) {
            await fetchDrilldownNodes();
        }
        return sports.value.find((i) => i.id === id);
    };
    // Keep in mind that not all sports has code defined (sometimes is null) hence use with caution
    const getSportByCode = async (code: string) => {
        await waitUntilInitialized();
        if (!sports.value.some((i) => i.code === code)) {
            await fetchDrilldownNodes();
        }
        return sports.value.find((i) => i.code === code);
    };
    const getCountryById = async (id: string) => {
        await waitUntilInitialized();
        if (!countries.value.some((i) => i.id === id)) {
            await fetchDrilldownNodes();
        }
        return countries.value.find((i) => i.id === id);
    };
    const getLeagueById = async (id: string) => {
        await waitUntilInitialized();
        if (!leagues.value.some((i) => i.id === id)) {
            await fetchDrilldownNodes();
        }
        return leagues.value.find((i) => i.id === id);
    };
    const getSportPriceBoostId = async (): Promise<string | undefined> => (await getSportByCode(Sport.PriceBoost))?.id;
    const setFetchInterval = (interval: number) => {
        fetchInterval.value = interval;
    };
    const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
        await waitUntilTrue(initialized, time, timeout).catch(() => {
            logException("drilldown-nodes", undefined, {
                message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
            });
        });
    };
    const fetchDrilldownNodes = async (force: boolean = false) => {
        // Prevent excessive endpoint calls... results under given time interval (ms) should be accurate enough
        if (!force && ((lastFetched.value && performance.now() - lastFetched.value < 3000) || fetchInProgress.value)) {
            return;
        }
        fetchInProgress.value = true;
        const nodes = await getDrilldownNodes();
        drilldownNodesTree.value = nodes || [];
        lastFetched.value = performance.now();
        fetchInProgress.value = false;
        return nodes;
    };

    return {
        fetchInterval,
        lastFetched,
        drilldownNodesTree,
        drilldownNodesFlat,
        all,
        sports,
        countries,
        leagues,
        priceBoostId,
        getDrilldownNodesById,
        getAllById,
        getSportById,
        getSportByCode,
        getCountryById,
        getLeagueById,
        getSportPriceBoostId,
        setFetchInterval,
        waitUntilInitialized,
        fetchDrilldownNodes,
    };
});

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useDrilldownNodesStore, import.meta.hot));
}
